import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Users, Plus, Trash2, Save } from 'lucide-react';
import * as YearEndService from '../../../../services/YearEndService';

const ROLLER = [
    ['bolagsman', 'Bolagsman (HB)'],
    ['komplementar', 'Komplementär (KB)'],
    ['kommanditdelagare', 'Kommanditdelägare (KB)'],
];

const TOM = {
    namn: '', personOrgNr: '', delagartyp: 'fysisk', roll: 'bolagsman',
    resultatandel: '', kapitalandel: '', ansvarsbelopp: '',
};

/**
 * Delägarregistret för HB/KB. Källa till INK4DU och till fördelningen av
 * årets resultat.
 *
 * Kontoplatsen (delägare 1-4 = BAS-serierna 2010-2019 … 2040-2049) tilldelas
 * av servern vid registrering och går inte att ändra: serien är personens,
 * inte andelens. Ändras andelarna ska ingen byta konto.
 */
const DelagarePanel = ({ companyId, fiscalYearId, onChanged }) => {
    const [delagare, setDelagare] = useState([]);
    const [utkast, setUtkast] = useState(TOM);
    const [visaNy, setVisaNy] = useState(false);
    const [sparar, setSparar] = useState(false);

    const hamta = useCallback(async () => {
        try {
            const resp = await YearEndService.listDelagare(companyId, fiscalYearId);
            setDelagare(resp?.data || []);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Kunde inte hämta delägarna.');
        }
    }, [companyId, fiscalYearId]);

    useEffect(() => { hamta(); }, [hamta]);

    const tal = (v) => (v === '' || v == null ? null : Number(String(v).replace(',', '.')));

    const skapa = async () => {
        setSparar(true);
        try {
            await YearEndService.createDelagare(companyId, {
                ...utkast,
                resultatandel: tal(utkast.resultatandel),
                kapitalandel: tal(utkast.kapitalandel),
                ansvarsbelopp: tal(utkast.ansvarsbelopp),
            });
            toast.success('Delägaren är registrerad.');
            setUtkast(TOM); setVisaNy(false);
            await hamta(); onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Kunde inte spara delägaren.');
        } finally { setSparar(false); }
    };

    const spara = async (d) => {
        try {
            await YearEndService.updateDelagare(companyId, d.delagareId, {
                ...d, resultatandel: tal(d.resultatandel), kapitalandel: tal(d.kapitalandel),
                ansvarsbelopp: tal(d.ansvarsbelopp),
            });
            if (fiscalYearId) {
                await YearEndService.saveDelagareArsuppgifter(companyId, d.delagareId, fiscalYearId, {
                    tillskott: tal(d.tillskott) || 0,
                    uttag: tal(d.uttag) || 0,
                    andelAvdragArbetsgivaravgift: tal(d.andelAvdragArbetsgivaravgift) || 0,
                });
            }
            toast.success('Sparat.');
            await hamta(); onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Kunde inte spara.');
        }
    };

    const taBort = async (d) => {
        try {
            await YearEndService.deleteDelagare(companyId, d.delagareId);
            toast.success('Delägaren är borttagen.');
            await hamta(); onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Kunde inte ta bort delägaren.');
        }
    };

    const andelssumma = delagare.reduce((s, d) => s + (Number(d.resultatandel) || 0), 0);
    const avvikerFran100 = delagare.length > 0 && Math.abs(andelssumma - 1) > 0.0001;

    const satt = (id, falt, v) => setDelagare((rows) => rows.map(
        (r) => (r.delagareId === id ? { ...r, [falt]: v } : r),
    ));

    return (
        <div className="ye-ps-section">
            <div className="d-flex align-items-center justify-content-between">
                <h5 className="ye-section-heading">
                    <Users size={16} style={{ marginRight: 6 }} />
                    Delägare
                </h5>
                <button
                    className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                    onClick={() => setVisaNy((v) => !v)}
                    disabled={delagare.length >= 4}
                >
                    <Plus size={14} />
                    Lägg till
                </button>
            </div>
            <p className="text-muted" style={{ maxWidth: '68ch' }}>
                Resultatandelen följer bolagsavtalet — den behöver varken motsvara
                kapitalandelen eller vara lika delar. Andelarna ger delägarnas belopp i
                bilagan INK4DU och styr hur årets resultat förs till kapitalkontona.
                {delagare.length >= 4 && ' Standardkontoplanen rymmer fyra delägare.'}
            </p>

            {delagare.length > 0 && (
                <div className="table-responsive">
                    <table className="table table-sm align-middle">
                        <thead>
                            <tr>
                                <th style={{ width: 40 }}>#</th>
                                <th>Namn</th>
                                <th>Person-/orgnr</th>
                                <th>Roll</th>
                                <th style={{ width: 110 }}>Resultatandel</th>
                                <th style={{ width: 110 }}>Tillskott</th>
                                <th style={{ width: 110 }}>Uttag</th>
                                <th style={{ width: 90 }} />
                            </tr>
                        </thead>
                        <tbody>
                            {delagare.map((d) => (
                                <tr key={d.delagareId}>
                                    <td className="text-muted">{d.delagareNr}</td>
                                    <td>
                                        <input className="form-control form-control-sm" value={d.namn || ''}
                                            onChange={(e) => satt(d.delagareId, 'namn', e.target.value)} />
                                    </td>
                                    <td>
                                        <input className="form-control form-control-sm" value={d.personOrgNr || ''}
                                            placeholder="ÅÅÅÅMMDD-NNNN"
                                            onChange={(e) => satt(d.delagareId, 'personOrgNr', e.target.value)} />
                                    </td>
                                    <td>
                                        <select className="form-select form-select-sm" value={d.roll || 'bolagsman'}
                                            onChange={(e) => satt(d.delagareId, 'roll', e.target.value)}>
                                            {ROLLER.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                        </select>
                                    </td>
                                    <td>
                                        <input className="form-control form-control-sm" inputMode="decimal"
                                            value={d.resultatandel ?? ''} placeholder="0,5"
                                            onChange={(e) => satt(d.delagareId, 'resultatandel', e.target.value)} />
                                    </td>
                                    <td>
                                        <input className="form-control form-control-sm" inputMode="decimal"
                                            value={d.tillskott ?? ''} placeholder="0"
                                            onChange={(e) => satt(d.delagareId, 'tillskott', e.target.value)} />
                                    </td>
                                    <td>
                                        <input className="form-control form-control-sm" inputMode="decimal"
                                            value={d.uttag ?? ''} placeholder="0"
                                            onChange={(e) => satt(d.delagareId, 'uttag', e.target.value)} />
                                    </td>
                                    <td>
                                        <div className="d-flex gap-1">
                                            <button className="btn btn-primary btn-sm" title="Spara"
                                                onClick={() => spara(d)}>
                                                <Save size={13} />
                                            </button>
                                            <button className="btn btn-outline-secondary btn-sm" title="Ta bort"
                                                onClick={() => taBort(d)}>
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {avvikerFran100 && (
                <div className="alert alert-warning mb-0" style={{ fontSize: '0.875rem' }}>
                    Andelarna summerar till {Math.round(andelssumma * 1000) / 10} %, inte 100 %.
                    Resultatet kan inte fördelas förrän de gör det.
                </div>
            )}

            {visaNy && (
                <div className="ye-adj-form" style={{ marginTop: '0.75rem' }}>
                    <div className="ye-form-grid">
                        <div className="ye-field">
                            <label htmlFor="ny-namn">Namn</label>
                            <input id="ny-namn" value={utkast.namn}
                                onChange={(e) => setUtkast({ ...utkast, namn: e.target.value })} />
                        </div>
                        <div className="ye-field">
                            <label htmlFor="ny-nr">Person-/organisationsnummer</label>
                            <input id="ny-nr" value={utkast.personOrgNr} placeholder="ÅÅÅÅMMDD-NNNN"
                                onChange={(e) => setUtkast({ ...utkast, personOrgNr: e.target.value })} />
                            <div className="ye-field-help">Obligatoriskt — blir fältkod 8100 i INK4DU.</div>
                        </div>
                        <div className="ye-field">
                            <label htmlFor="ny-roll">Roll</label>
                            <select id="ny-roll" className="form-select" value={utkast.roll}
                                onChange={(e) => setUtkast({ ...utkast, roll: e.target.value })}>
                                {ROLLER.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                            <div className="ye-field-help">
                                Bara obegränsat ansvariga delägare skriver under årsbokslutet.
                            </div>
                        </div>
                        <div className="ye-field">
                            <label htmlFor="ny-andel">Resultatandel (0–1)</label>
                            <input id="ny-andel" inputMode="decimal" value={utkast.resultatandel}
                                placeholder="0,5"
                                onChange={(e) => setUtkast({ ...utkast, resultatandel: e.target.value })} />
                        </div>
                        <div className="ye-field">
                            <label htmlFor="ny-ansvar">Insatt/åtagit sig att sätta in (kr)</label>
                            <input id="ny-ansvar" inputMode="decimal" value={utkast.ansvarsbelopp}
                                onChange={(e) => setUtkast({ ...utkast, ansvarsbelopp: e.target.value })} />
                            <div className="ye-field-help">
                                Taket för en kommanditdelägares underskottsavdrag.
                            </div>
                        </div>
                    </div>
                    <div className="ye-proposal-actions">
                        <button className="btn btn-primary" onClick={skapa} disabled={sparar}>
                            {sparar ? 'Sparar…' : 'Registrera delägare'}
                        </button>
                        <button className="btn btn-outline-secondary" onClick={() => setVisaNy(false)}>
                            Avbryt
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DelagarePanel;
